import { useState } from 'react'
import { IconoEnviar, IconoReloj, IconoReanudar } from '../../componentes/iconos'
import type { Canal } from './consultas'
import { useVentana, useBot, useEnviarMensaje, useReanudarBot } from './consultas'

interface Props {
  canal: Canal
  sesionId: string
  /** Teléfono del contacto (destino). Sin él no se puede enviar. */
  telefono: string | null
  /** Nombre del operador logueado (para la burbuja y la pausa). */
  operador?: string
}

/** Formatea "23h 59m" / "45m" / "ahora" a partir de milisegundos restantes. */
function restante(ms: number): string {
  if (ms <= 0) return 'cerrada'
  const min = Math.floor(ms / 60000)
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

/**
 * Chip de estado de la ventana de 24h de Meta, para la cabecera del chat (opción A).
 * Verde = abierta con cuenta atrás; ámbar = cerrada.
 */
export function ChipVentana({ canal, sesionId }: { canal: Canal; sesionId: string }) {
  const { data } = useVentana(canal, sesionId)
  if (!data) return null
  const abierta = data.abierta
  const ms = data.expira_en ? new Date(data.expira_en).getTime() - Date.now() : 0
  return (
    <span
      data-tour="conv-ventana"
      className={[
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold',
        abierta
          ? 'border-positivo-anillo bg-positivo-fondo text-positivo-tinta'
          : 'border-aviso-borde bg-aviso-fondo text-aviso-tinta',
      ].join(' ')}
      title={
        abierta
          ? 'Puedes responder con texto libre dentro de la ventana de 24h'
          : 'La ventana de 24h está cerrada. Para reabrir hay que iniciar con una plantilla.'
      }
    >
      <span className={`size-1.5 rounded-full ${abierta ? 'bg-positivo-punto' : 'bg-aviso-icono'}`} />
      {abierta ? `Ventana abierta · quedan ${restante(ms)}` : 'Ventana cerrada'}
    </span>
  )
}

/**
 * Compositor del chat (opción A): cuadro de texto + botón enviar abajo, y una
 * línea fina de "bot pausado" cuando el operador ha tomado la conversación.
 * Fuera de la ventana de 24h el envío de texto libre se desactiva con aviso.
 */
export function Compositor({ canal, sesionId, telefono, operador }: Props) {
  const [texto, setTexto] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)
  const ventana = useVentana(canal, sesionId)
  const bot = useBot(canal, telefono)
  const enviar = useEnviarMensaje(canal, sesionId, telefono)
  const reanudar = useReanudarBot(canal, telefono)

  const abierta = ventana.data?.abierta ?? false
  const puedeEnviar = abierta && !!telefono && texto.trim().length > 0 && !enviar.isPending
  const pausado = bot.data?.pausado ?? false

  const hacerEnvio = async () => {
    setAviso(null)
    if (!telefono) {
      setAviso('Esta conversación no tiene un teléfono al que escribir.')
      return
    }
    const res = await enviar.mutateAsync({ texto: texto.trim(), operador })
    if (!res.ok) {
      setAviso(res.motivo ?? 'No se pudo enviar el mensaje.')
      return
    }
    setTexto('')
  }

  return (
    <div className="border-t border-borde bg-superficie" data-tour="conv-compositor">
      {/* Línea de estado del bot (pausado) */}
      {pausado ? (
        <div className="flex items-center gap-2 border-b border-borde-suave px-5 py-2 text-[12px] text-tinta-apagada">
          <span className="size-1.5 shrink-0 rounded-full bg-aviso-icono" />
          <span>
            Bot <span className="font-semibold text-aviso-tinta">pausado</span> para este contacto
            {bot.data?.operador ? ` (lo tomó ${bot.data.operador})` : ' (lo tomaste tú)'}
          </span>
          <button
            type="button"
            onClick={() => reanudar.mutate()}
            disabled={reanudar.isPending}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-borde bg-lienzo px-2.5 py-1 text-[11px] font-semibold text-tinta transition-colors hover:bg-elevado disabled:opacity-50"
          >
            <IconoReanudar aria-hidden="true" className="size-3.5" />
            Devolver al bot
          </button>
        </div>
      ) : null}

      {/* Aviso de ventana cerrada / error de envío */}
      {!abierta ? (
        <div className="flex items-center gap-2 bg-aviso-fondo px-5 py-2 text-[12px] text-aviso-tinta">
          <IconoReloj aria-hidden="true" className="size-4 shrink-0" />
          <span>
            Ventana de 24h cerrada. Solo puedes escribir texto libre si el cliente ha respondido en
            las últimas 24h.
          </span>
        </div>
      ) : aviso ? (
        <div className="flex items-center gap-2 bg-aviso-fondo px-5 py-2 text-[12px] text-aviso-tinta">
          <span>{aviso}</span>
        </div>
      ) : null}

      {/* Cuadro de texto + enviar */}
      <div className="flex items-end gap-2 px-4 py-3">
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && puedeEnviar) {
              e.preventDefault()
              void hacerEnvio()
            }
          }}
          disabled={!abierta}
          rows={1}
          placeholder={abierta ? 'Escribe una respuesta…' : 'Texto libre no disponible fuera de 24h'}
          aria-label="Escribe una respuesta"
          className="max-h-32 min-h-10 flex-1 resize-none rounded-lg border border-borde bg-lienzo px-3 py-2 text-sm text-tinta outline-none placeholder:text-tinta-apagada focus:border-acento disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => void hacerEnvio()}
          disabled={!puedeEnviar}
          aria-label="Enviar mensaje"
          className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-acento text-white transition-opacity hover:opacity-90 disabled:bg-elevado disabled:text-tinta-apagada"
        >
          <IconoEnviar aria-hidden="true" className="size-5" />
        </button>
      </div>
    </div>
  )
}
